import React from "react";
import { FormattedText } from "../FormattedText";
import { safeTrim } from "./builderUtils";

function parseAsteriskParts(text) {
  const parts = [];
  const regex = /\*([^*]+)\*/g;
  let last = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    const start = match.index;
    const end = regex.lastIndex;

    if (start > last) {
      parts.push({ type: "text", value: text.slice(last, start) });
    }

    parts.push({ type: "blank", value: match[1] || "" });
    last = end;
  }

  if (last < text.length) {
    parts.push({ type: "text", value: text.slice(last) });
  }

  if (parts.length === 0) {
    parts.push({ type: "text", value: text });
  }

  return parts;
}

export function BuilderInteractionPreview({ interaction, onClose }) {
  const previewMedia = {
    backgroundImageUrl: safeTrim(interaction.backgroundImageUrl),
    exampleImageUrl: safeTrim(interaction.exampleImageUrl)
  };

  const cardClassName = `builder-interaction-preview ${
    interaction.type === "info_hint" ? "is-hint" : ""
  }`;

  const cardStyle = {
    left: `${interaction.position.xPct}%`,
    top: `${interaction.position.yPct}%`,
    ...(interaction.type !== "info_hint" &&
    previewMedia.backgroundImageUrl
      ? {
          backgroundImage: `linear-gradient(rgba(255,255,255,0.9), rgba(255,255,255,0.92)), url(${previewMedia.backgroundImageUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center"
        }
      : {})
  };

  return (
    <div className={cardClassName} style={cardStyle}>
      <button type="button" className="builder-preview-close" onClick={onClose}>
        ×
      </button>

      {interaction.type === "multiple_choice" && (
        <>
          {previewMedia.exampleImageUrl && (
            <img
              src={previewMedia.exampleImageUrl}
              alt="Örnek"
              className="interaction-example-image"
            />
          )}

          <h3 className="builder-preview-title">
            <FormattedText text={interaction.prompt} />
          </h3>

          <div className="mc-options">
            {interaction.options.slice(0, 5).map((option, idx) => (
              <button
                className="mc-option"
                key={option.text + idx}
                disabled
              >
                <span className="mc-dot">○</span>
                <span>
                  <FormattedText text={option.text} />
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      {interaction.type === "true_false" && (
        <>
          {previewMedia.exampleImageUrl && (
            <img
              src={previewMedia.exampleImageUrl}
              alt="Örnek"
              className="interaction-example-image"
            />
          )}

          <h3 className="builder-preview-title">
            <FormattedText text={interaction.prompt} />
          </h3>

          <select disabled defaultValue="true">
            <option value="true">Doğru</option>
            <option value="false">Yanlış</option>
          </select>
        </>
      )}

      {interaction.type === "fill_blank" && (
        <>
          {previewMedia.exampleImageUrl && (
            <img
              src={previewMedia.exampleImageUrl}
              alt="Örnek"
              className="interaction-example-image"
            />
          )}

          <h3 className="fill-blank-title">
            Boş bırakılan yeri doldurun.
          </h3>

          <div className="fill-blank-sentence">
            {parseAsteriskParts(
              interaction.templateText || interaction.prompt
            ).map((part, idx) =>
              part.type === "text" ? (
                <span key={`fb_pt_${idx}`}>
                  <FormattedText text={part.value} />
                </span>
              ) : (
                <input
                  key={`fb_pb_${idx}`}
                  className="fill-blank-input-inline"
                  value=""
                  placeholder="cevap"
                  disabled
                  readOnly
                />
              )
            )}
          </div>
        </>
      )}

      {interaction.type === "drag_drop" && (
        <>
          {previewMedia.exampleImageUrl && (
            <img
              src={previewMedia.exampleImageUrl}
              alt="Örnek"
              className="interaction-example-image"
            />
          )}

          <h3 className="drag-drop-title">
            <FormattedText
              text={interaction.prompt || "Sürükle Bırak Etkinliği"}
            />
          </h3>

          <div className="drag-drop-board">
            <div className="drag-drop-slots">
              {interaction.slots.map((slot, idx) => (
                <div className="drag-drop-slot-row" key={slot + idx}>
                  <p className="drag-drop-slot-label">
                    <FormattedText text={slot} />
                  </p>
                  <div className="drag-drop-slot" />
                </div>
              ))}
            </div>

            <div className="drag-drop-bank">
              {interaction.items.map((item, idx) => (
                <button
                  className="drag-drop-chip"
                  key={item + idx}
                  disabled
                >
                  <FormattedText text={item} />
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {interaction.type === "word_bank_fill" && (
        <>
          {previewMedia.exampleImageUrl && (
            <img
              src={previewMedia.exampleImageUrl}
              alt="Örnek"
              className="interaction-example-image"
            />
          )}

          <h3 className="builder-preview-title">
            <FormattedText text={interaction.prompt} />
          </h3>

          <div className="word-bank-sentence">
            {parseAsteriskParts(interaction.sentenceTemplate).map(
              (part, idx) =>
                part.type === "text" ? (
                  <span key={`wb_t_${idx}`}>
                    <FormattedText text={part.value} />
                  </span>
                ) : (
                  <button
                    className="word-bank-blank"
                    key={`wb_b_${idx}`}
                    disabled
                  />
                )
            )}
          </div>

          <div className="word-bank-pool">
            {[...interaction.correctWords, ...interaction.distractorWords].map(
              (word, idx) => (
                <button
                  className="word-bank-chip"
                  key={word + idx}
                  disabled
                >
                  <FormattedText text={word} />
                </button>
              )
            )}
          </div>
        </>
      )}

      {interaction.type === "info_hint" && (
        <div
          className="hint-card builder-hint-preview"
          style={
            previewMedia.backgroundImageUrl
              ? {
                  backgroundImage: `linear-gradient(rgba(15,23,42,0.82), rgba(15,23,42,0.86)), url(${previewMedia.backgroundImageUrl})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center"
                }
              : undefined
          }
        >
          <strong>Bilgi</strong>

          {previewMedia.exampleImageUrl && (
            <img
              src={previewMedia.exampleImageUrl}
              alt="İpucu görseli"
              className="interaction-example-image hint-example"
            />
          )}

          <p>
            <FormattedText text={interaction.prompt} />
          </p>
        </div>
      )}
    </div>
  );
}
